import React from 'react';
import { Stethoscope, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fmtWhen } from '../../lib/doctorView';
import { tone, LevelIcon, DoctorFix, HoldWhen } from './DoctorParts';

/*
 * r16sd (design 5.3): the slot banner, modelled on NotWiredBanner. It shows the slot's holds - why a feature the customer ticked
 * is held back (0 post credits, a pause, an admin switch, a phone ...) - in the server's own words, red first.
 *
 *   variant "line"  under the slot label in the Slots table: the dot and the first hold, two lines at most, "+N" for the rest;
 *                   a click opens the doctor drawer (when there is one);
 *   variant "strip" the same in a phone card, full width;
 *   variant "page"  the slot settings page header: every hold as its own banner with its fix.
 * A row with findings only (no hold) says how many settings to check. Nothing is drawn when there is nothing to show.
 * `applyBlocked` (the settings page with unsaved edits): a one-click fix opens its setting instead (stage 5, DoctorFix).
 */
export default function SlotBanner({
  holds, attention, variant = 'line', slotName, deviceId, onOpen, onFocusSetting, onFixed, testid, applyBlocked,
}) {
  const list = Array.isArray(holds) ? holds : [];
  const findings = attention && attention.findings ? attention.findings : 0;
  if (!list.length && !findings) return null;
  const tid = testid || `slot-banner-${slotName}`;

  if (variant === 'page') {
    return (
      <div className="space-y-2" data-testid={tid} role="status">
        {list.map((h, i) => {
          const t = tone(h.level);
          return (
            <div
              key={`${h.rule}-${i}`}
              className={cn('flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:gap-4 sm:p-5', t.box)}
              data-testid={`${tid}-hold-${h.rule}`}
              data-level={h.level}
            >
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className={cn('mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ring-1', t.chip)}>
                  <LevelIcon level={h.level} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-relaxed text-foreground text-pretty">{h.text}</p>
                  <HoldWhen hold={h} fmt={fmtWhen} className="mt-1 block" />
                </div>
              </div>
              {h.fix && (
                <div className="flex shrink-0 sm:justify-end">
                  <DoctorFix fix={h.fix} settings={h.settings} slotName={slotName} deviceId={deviceId} onFocusSetting={onFocusSetting}
                    onDone={onFixed} testid={`${tid}-fix-${h.rule}`} rule={h.rule} nearText={h.text} applyBlocked={applyBlocked} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  const first = list[0] || null;
  const level = first ? first.level : (attention && attention.level) || 'amber';
  const more = Math.max(0, list.length - 1);
  // findings only: a count, never a sentence of ours about a rule
  const body = first ? first.text : `${findings} setting${findings === 1 ? '' : 's'} to check`;
  const meta = [more > 0 ? `+${more} more` : null, first && findings ? `${findings} to check` : null].filter(Boolean).join(' · ');
  const Tag = onOpen ? 'button' : 'div';
  const clickProps = onOpen ? { type: 'button', onClick: (e) => { e.stopPropagation(); onOpen(); } } : {};

  if (variant === 'strip') {
    const t = tone(level);
    return (
      <Tag
        {...clickProps}
        className={cn('flex w-full min-w-0 items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left', t.box, onOpen && 'tap hover:brightness-110')}
        data-testid={tid}
        data-level={level}
        aria-label={onOpen ? `Check ${slotName}: ${body}` : undefined}
      >
        <LevelIcon level={level} className="mt-0.5" />
        <span className="min-w-0 flex-1">
          <span className="line-clamp-3 text-xs leading-relaxed text-foreground/90">{body}</span>
          {meta && <span className="mt-1 block text-[11px] text-muted-foreground">{meta}</span>}
        </span>
        {onOpen && <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />}
      </Tag>
    );
  }

  // "line": the row's own line under the slot (the Slots table draws it as a row of its own across the columns): one line, the
  // whole list in the title
  return (
    <Tag
      {...clickProps}
      className={cn('flex w-full max-w-[60rem] min-w-0 items-center gap-2 text-left', onOpen && 'group/banner')}
      title={list.length > 1 ? list.map((h) => h.text).join('\n') : body}
      data-testid={tid}
      data-level={level}
      aria-label={onOpen ? `Check ${slotName}: ${body}` : undefined}
    >
      <LevelIcon level={level} className="h-3.5 w-3.5" />
      <span className="min-w-0 truncate text-xs text-foreground/85 group-hover/banner:text-foreground">{body}</span>
      {meta && <span className="shrink-0 text-xs font-medium text-muted-foreground">{meta}</span>}
      {onOpen && (
        <span className="ml-1 inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary/80 group-hover/banner:text-primary">
          <Stethoscope className="h-3.5 w-3.5" aria-hidden /> Check
        </span>
      )}
    </Tag>
  );
}
